<template>
  <div class="stock-movements">
    <div class="page-header">
      <h2>出入库记录</h2>
    </div>
    
    <a-card class="filter-card">
      <a-form
        :model="searchForm"
        layout="inline"
        @finish="handleSearch"
        class="search-form"
      >
        <a-form-item label="变动类型" name="movementType">
          <a-select
            v-model:value="searchForm.movementType"
            placeholder="请选择类型"
            style="width: 120px"
            allowClear
          >
            <a-select-option value="IN">入库</a-select-option>
            <a-select-option value="OUT">出库</a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item label="原因类型" name="reasonType">
          <a-select
            v-model:value="searchForm.reasonType"
            placeholder="请选择原因"
            style="width: 140px"
            allowClear
            :disabled="!searchForm.movementType"
          >
            <template v-if="searchForm.movementType === 'IN'">
              <a-select-option value="production">生产入库</a-select-option>
              <a-select-option value="return">退货入库</a-select-option>
              <a-select-option value="other">其他入库</a-select-option>
            </template>
            <template v-else-if="searchForm.movementType === 'OUT'">
              <a-select-option value="sales">销售出库</a-select-option>
              <a-select-option value="other">其他出库</a-select-option>
            </template>
          </a-select>
        </a-form-item>

        <a-form-item label="时间范围" name="dateRange">
          <a-range-picker
            v-model:value="searchForm.dateRange"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            style="width: 240px"
            :disabled-date="disabledDate"
          />
        </a-form-item>
        
        <a-form-item label="记录日期" name="recordDate">
          <a-date-picker
            v-model:value="searchForm.recordDate"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            style="width: 160px"
            :disabled-date="disabledDate"
            placeholder="指定日期查询"
            allowClear
          />
        </a-form-item>
        
        <a-form-item label="商品名称" name="productName">
          <a-input
            v-model:value="searchForm.productName"
            placeholder="输入商品名称搜索"
            style="width: 200px"
            allow-clear
            @change="handleProductNameChange"
          />
        </a-form-item>
        
        <a-form-item>
          <a-button type="primary" html-type="submit" :loading="loading">
            <template #icon>
              <SearchOutlined />
            </template>
            搜索
          </a-button>
          <a-button style="margin-left: 8px" @click="handleReset">
            重置
          </a-button>
        </a-form-item>
      </a-form>
    </a-card>
    
    <a-card>
      <div class="records-list">
        <div v-if="loading" class="loading-container">
          <a-spin size="large" />
        </div>
        
        <div v-else-if="recordsList.length === 0" class="empty-container">
          <a-empty description="暂无出入库记录" />
        </div>
        
        <div v-else>
          <div 
            v-for="record in recordsList" 
            :key="record.id" 
            class="record-item"
          >
            <!-- 记录头部信息 -->
            <div class="record-header">
              <div class="record-info">
                <div class="record-date">
                  <a-tag :color="getMovementTypeColor(record.movementType)" size="large">
                    {{ record.movementTypeName }}
                  </a-tag>
                  <span class="date">{{ formatRecordDate(record.recordDate) }}</span>
                </div>
                <div class="record-summary">
                  <span class="quantity">总数量: {{ record.quantity }}</span>
                  <span class="amount">总金额: ¥{{ formatAmount(record.totalAmount) }}</span>
                </div>
              </div>
              <div class="record-meta">
                <div class="create-info">
                  创建时间: {{ formatDateTime(record.createTime) }}
                </div>
                <div v-if="record.remark" class="remark">
                  备注: {{ record.remark }}
                </div>
              </div>
            </div>
            
            <!-- 商品列表 -->
            <div class="products-list">
              <div 
                v-for="product in record.products" 
                :key="product.productId" 
                class="product-item"
              >
                <!-- 商品信息 -->
                <div class="product-header">
                  <div class="product-image">
                    <a-image
                      v-if="product.imageUrls && product.imageUrls.length > 0"
                      :width="60"
                      :height="60"
                      :src="$thumb(product.imageUrls[0])"
                      :preview="false"
                      style="border-radius: 4px; object-fit: cover;"
                    />
                    <div v-else class="no-image">暂无图片</div>
                  </div>
                  <div class="product-info">
                    <div class="product-name">{{ product.productName }}</div>
                    <div class="product-details">
                      <span class="warehouse">{{ product.warehouseName }}</span>
                      <span class="reason">{{ formatReasonType(product.reasonType, record.movementType) }}</span>
                    </div>
                    <div class="product-summary">
                      数量: {{ product.productQuantity }} | 金额: ¥{{ formatAmount(product.productTotalAmount) }}
                    </div>
                  </div>
                </div>
                
                <!-- SKU列表 -->
                <div class="skus-list">
                  <div 
                    v-for="sku in product.skus" 
                    :key="sku.movementId" 
                    class="sku-item"
                  >
                    <div class="sku-info">
                      <div class="sku-specs">
                        <span class="color">{{ sku.color || '默认' }}</span>
                        <span class="size">{{ sku.size || '默认' }}</span>
                      </div>
                      <div class="sku-quantity">
                        <span :style="{ color: getQuantityColor(record.movementType) }">
                          {{ getQuantityDisplay(record.movementType, sku.quantity) }}
                        </span>
                      </div>
                      <div class="sku-price" v-if="sku.unitPrice">
                        单价: ¥{{ formatAmount(sku.unitPrice) }}
                      </div>
                      <div class="sku-total">
                        总价: ¥{{ formatAmount(sku.totalAmount) }}
                      </div>
                      <div class="sku-actions">
                        <a-button
                          type="link"
                          size="small"
                          @click="startEditSku(sku, product, record)"
                          style="padding: 0 4px;"
                        >
                          编辑
                        </a-button>
                        <a-popconfirm
                          title="确定要删除这个SKU记录吗？"
                          @confirm="deleteSku(sku)"
                          ok-text="确定"
                          cancel-text="取消"
                        >
                          <a-button
                            type="link"
                            danger
                            size="small"
                            style="padding: 0 4px;"
                          >
                            删除
                          </a-button>
                        </a-popconfirm>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 分页 -->
      <div class="pagination-container" v-if="recordsList.length > 0">
        <a-pagination
          v-model:current="pagination.current"
          v-model:page-size="pagination.pageSize"
          :total="pagination.total"
          :show-size-changer="true"
          :show-quick-jumper="true"
          :show-total="(total) => `共 ${total} 条记录`"
          @change="handlePageChange"
          @show-size-change="handlePageSizeChange"
        />
      </div>
    </a-card>

    <!-- 编辑SKU模态框 -->
    <a-modal
      v-model:open="editModalVisible"
      title="编辑库存变动记录"
      :width="600"
      @ok="confirmEdit"
      @cancel="cancelEdit"
      :confirmLoading="editLoading"
    >
      <a-form
        :model="editForm"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 16 }"
      >
        <a-form-item label="商品">
          <span>{{ editForm.productName }}</span>
        </a-form-item>

        <a-form-item label="原仓库">
          <span>{{ editForm.originalWarehouseName }}</span>
        </a-form-item>

        <a-form-item label="新仓库" name="warehouseId">
          <a-select
            v-model:value="editForm.warehouseId"
            placeholder="选择仓库"
            :loading="warehouseLoading"
          >
            <a-select-option
              v-for="warehouse in warehouseList"
              :key="warehouse.id"
              :value="warehouse.id"
            >
              {{ warehouse.name }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item label="颜色" name="color">
          <a-select
            v-model:value="editForm.color"
            placeholder="选择颜色"
            allow-clear
            show-search
          >
            <a-select-option
              v-for="color in colorOptions"
              :key="color.value"
              :value="color.value"
            >
              {{ color.value }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item label="尺寸" name="size">
          <a-select
            v-model:value="editForm.size"
            placeholder="选择尺寸"
            allow-clear
            show-search
          >
            <a-select-option
              v-for="size in sizeOptions"
              :key="size.value"
              :value="size.value"
            >
              {{ size.value }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item label="数量" name="quantity" required>
          <a-input-number
            v-model:value="editForm.quantity"
            :min="1"
            style="width: 100%;"
            placeholder="输入数量"
          />
        </a-form-item>

        <a-form-item label="单价" name="unitPrice">
          <a-input-number
            v-model:value="editForm.unitPrice"
            :min="0"
            :precision="2"
            style="width: 100%;"
            placeholder="输入单价"
          />
        </a-form-item>

        <a-form-item label="原因类型" name="reasonType">
          <a-select
            v-model:value="editForm.reasonType"
            placeholder="选择原因类型"
          >
            <template v-if="editForm.movementType === 'IN'">
              <a-select-option value="production">生产入库</a-select-option>
              <a-select-option value="return">退货入库</a-select-option>
              <a-select-option value="other">其他入库</a-select-option>
            </template>
            <template v-else-if="editForm.movementType === 'OUT'">
              <a-select-option value="sales">销售出库</a-select-option>
              <a-select-option value="other">其他出库</a-select-option>
            </template>
          </a-select>
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { ref, shallowRef, reactive, onMounted, watch } from 'vue'
import { message } from 'ant-design-vue'
import { SearchOutlined } from '@ant-design/icons-vue'
import { stockApi, warehouseApi, productApi } from '@/api'
import dayjs from 'dayjs'

export default {
  name: 'StockMovements',
  components: {
    SearchOutlined
  },
  setup() {
    const loading = ref(false)
    const recordsList = shallowRef([])  // 纯展示大列表：shallowRef 免深度响应式
    const editModalVisible = ref(false)
    const editLoading = ref(false)
    const warehouseLoading = ref(false)
    const warehouseList = ref([])
    const colorOptions = ref([])
    const sizeOptions = ref([])

    const searchForm = reactive({
      movementType: undefined,
      reasonType: undefined,
      dateRange: undefined,
      recordDate: undefined,
      productName: ''
    })

    const editForm = reactive({
      movementId: null,
      productName: '',
      originalWarehouseName: '',
      warehouseId: null,
      color: '',
      size: '',
      quantity: 1,
      unitPrice: 0,
      reasonType: '',
      movementType: ''
    })

    // 监听变动类型变化,自动清空原因类型
    watch(() => searchForm.movementType, (newVal, oldVal) => {
      if (oldVal !== undefined && newVal !== oldVal) {
        searchForm.reasonType = undefined
      }
    })
    
    const pagination = reactive({
      current: 1,
      pageSize: 10,
      total: 0
    })
    
    const loadRecords = async () => {
      loading.value = true
      try {
        const params = {
          pageNo: pagination.current,
          pageSize: pagination.pageSize,
          ...searchForm
        }
        
        // 处理时间范围
        if (searchForm.dateRange && searchForm.dateRange.length === 2) {
          params.startTime = dayjs(searchForm.dateRange[0]).format('YYYYMMDD')
          params.endTime = dayjs(searchForm.dateRange[1]).format('YYYYMMDD')
        }
        
        // 处理指定记录日期
        if (searchForm.recordDate) {
          params.recordDate = dayjs(searchForm.recordDate).format('YYYYMMDD')
        }
        
        // 删除不需要发送给后端的字段
        delete params.dateRange
        
        const result = await stockApi.getRecords(params)
        recordsList.value = result.data.records || []
        pagination.total = result.data.total || 0
      } catch (error) {
        console.error('加载出入库记录失败:', error)
        message.error('加载出入库记录失败')
      } finally {
        loading.value = false
      }
    }
    
    const handleSearch = () => {
      pagination.current = 1
      loadRecords()
    }
    
    const handleReset = () => {
      Object.assign(searchForm, {
        movementType: undefined,
        reasonType: undefined,
        dateRange: undefined,
        recordDate: undefined,
        productName: ''
      })
      pagination.current = 1
      loadRecords()
    }

    const handleProductNameChange = () => {
      pagination.current = 1
      loadRecords()
    }
    
    const handlePageChange = (page, pageSize) => {
      pagination.current = page
      pagination.pageSize = pageSize
      loadRecords()
    }
    
    const handlePageSizeChange = (current, size) => {
      pagination.current = 1
      pagination.pageSize = size
      loadRecords()
    }
    
    const disabledDate = (current) => {
      return current && current > dayjs().endOf('day')
    }
    
    const getMovementTypeColor = (type) => {
      const colorMap = {
        'IN': 'green',
        'OUT': 'red',
        'ADJUST': 'orange'
      }
      return colorMap[type] || 'default'
    }
    
    const getQuantityColor = (type) => {
      if (type === 'IN') return '#52c41a'
      if (type === 'OUT') return '#ff4d4f'
      return '#fa8c16'
    }
    
    const getQuantityDisplay = (type, quantity) => {
      if (type === 'IN') return `+${quantity}`
      if (type === 'OUT') return `-${quantity}`
      return quantity > 0 ? `+${quantity}` : quantity.toString()
    }
    
    const formatRecordDate = (recordDate) => {
      if (!recordDate) return '-'
      
      const str = recordDate.toString()
      
      if (str.length === 8) {
        const year = str.substring(0, 4)
        const month = str.substring(4, 6)
        const day = str.substring(6, 8)
        return `${year}-${month}-${day}`
      }
      
      try {
        return dayjs(recordDate).format('YYYY-MM-DD')
      } catch (error) {
        return recordDate
      }
    }

    const formatDateTime = (dateTimeStr) => {
      if (!dateTimeStr) return '-'
      
      const str = dateTimeStr.toString()
      
      if (str.length === 14) {
        const year = str.substring(0, 4)
        const month = str.substring(4, 6)
        const day = str.substring(6, 8)
        const hour = str.substring(8, 10)
        const minute = str.substring(10, 12)
        const second = str.substring(12, 14)
        
        return `${year}-${month}-${day} ${hour}:${minute}:${second}`
      }
      
      if (str.length === 13) {
        const timestamp = parseInt(str)
        if (timestamp) {
          return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss')
        }
      }
      
      if (str.length === 10) {
        const timestamp = parseInt(str) * 1000
        if (timestamp) {
          return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss')
        }
      }
      
      return dateTimeStr
    }
    
    const formatAmount = (amount) => {
      if (amount === null || amount === undefined) {
        return '0.00'
      }

      const numAmount = Number(amount)
      if (isNaN(numAmount)) {
        return '0.00'
      }

      return numAmount.toFixed(2)
    }

    const formatReasonType = (reasonType, movementType) => {
      const reasonTypeMap = {
        'production': '生产入库',
        'return': '退货入库',
        'sales': '销售出库'
      }

      // 处理"其他"类型,根据入库/出库区分
      if (reasonType === 'other') {
        return movementType === 'IN' ? '其他入库' : '其他出库'
      }

      return reasonTypeMap[reasonType] || reasonType || '-'
    }

    const loadWarehouses = async () => {
      warehouseLoading.value = true
      try {
        const result = await warehouseApi.list({})
        warehouseList.value = result.data || []
      } catch (error) {
        console.error('加载仓库列表失败:', error)
        message.error('加载仓库列表失败')
      } finally {
        warehouseLoading.value = false
      }
    }

    const loadAttributes = async () => {
      try {
        const [colors, sizes] = await Promise.all([
          productApi.getAttributes('color'),
          productApi.getAttributes('size')
        ])
        console.log('颜色数据:', colors)
        console.log('尺寸数据:', sizes)
        colorOptions.value = colors.data || []
        sizeOptions.value = sizes.data || []
        console.log('颜色选项:', colorOptions.value)
        console.log('尺寸选项:', sizeOptions.value)
      } catch (error) {
        console.error('加载属性选项失败:', error)
        message.error('加载属性选项失败')
      }
    }

    const startEditSku = async (sku, product, record) => {
      // 并行加载仓库列表和属性选项
      await Promise.all([
        loadWarehouses(),
        loadAttributes()
      ])

      // 填充编辑表单
      Object.assign(editForm, {
        movementId: sku.movementId,
        productName: product.productName,
        originalWarehouseName: product.warehouseName,
        warehouseId: product.warehouseId,
        color: sku.color || '',
        size: sku.size || '',
        quantity: sku.quantity,
        unitPrice: sku.unitPrice || 0,
        reasonType: product.reasonType,
        movementType: record.movementType
      })

      editModalVisible.value = true
    }

    const confirmEdit = async () => {
      if (!editForm.quantity || editForm.quantity <= 0) {
        message.error('数量必须大于0')
        return
      }

      if (!editForm.warehouseId) {
        message.error('请选择仓库')
        return
      }

      try {
        editLoading.value = true

        // 构建更新数据
        const updateData = {
          warehouseId: editForm.warehouseId,
          color: editForm.color,
          size: editForm.size,
          quantity: editForm.quantity,
          unitPrice: editForm.unitPrice,
          reasonType: editForm.reasonType
        }

        // 调用新的 updateMovement 方法
        await stockApi.updateMovement(editForm.movementId, updateData)

        message.success('库存变动记录修改成功')
        editModalVisible.value = false
        loadRecords()
      } catch (error) {
        console.error('修改库存变动记录失败:', error)
        message.error(error.message || '修改库存变动记录失败')
      } finally {
        editLoading.value = false
      }
    }

    const cancelEdit = () => {
      editModalVisible.value = false
      // 重置表单
      Object.assign(editForm, {
        movementId: null,
        productName: '',
        originalWarehouseName: '',
        warehouseId: null,
        color: '',
        size: '',
        quantity: 1,
        unitPrice: 0,
        reasonType: '',
        movementType: ''
      })
    }
    
    const deleteSku = async (sku) => {
      try {
        loading.value = true
        await stockApi.deleteSku(sku.movementId)
        message.success('SKU记录删除成功')
        loadRecords()
      } catch (error) {
        console.error('删除SKU记录失败:', error)
        message.error('删除SKU记录失败')
      } finally {
        loading.value = false
      }
    }
    
    onMounted(() => {
      loadRecords()
    })
    
    return {
      loading,
      recordsList,
      searchForm,
      pagination,
      editModalVisible,
      editLoading,
      warehouseLoading,
      warehouseList,
      colorOptions,
      sizeOptions,
      editForm,
      handleSearch,
      handleReset,
      handleProductNameChange,
      handlePageChange,
      handlePageSizeChange,
      disabledDate,
      getMovementTypeColor,
      getQuantityColor,
      getQuantityDisplay,
      formatRecordDate,
      formatDateTime,
      formatAmount,
      formatReasonType,
      startEditSku,
      confirmEdit,
      cancelEdit,
      deleteSku
    }
  }
}
</script>

<style scoped>
.stock-movements {
  padding: 0;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
}

.filter-card {
  margin-bottom: 16px;
}

.search-form .ant-form-item {
  margin-bottom: 0;
}

.records-list {
  min-height: 400px;
}

.loading-container,
.empty-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
}

.record-item {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  margin-bottom: 16px;
  overflow: hidden;
}

.record-header {
  background: #fafafa;
  padding: 16px;
  border-bottom: 1px solid #e8e8e8;
}

.record-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.record-date {
  display: flex;
  align-items: center;
  gap: 12px;
}

.record-date .date {
  font-size: 16px;
  font-weight: 500;
}

.record-summary {
  display: flex;
  gap: 24px;
  font-size: 14px;
  color: #666;
}

.record-meta {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #999;
}

.products-list {
  padding: 0;
}

.product-item {
  border-bottom: 1px solid #f0f0f0;
  padding: 16px;
}

.product-item:last-child {
  border-bottom: none;
}

.product-header {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.product-image {
  flex-shrink: 0;
}

.no-image {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  font-size: 12px;
  color: #999;
}

.product-info {
  flex: 1;
}

.product-name {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 4px;
}

.product-details {
  display: flex;
  gap: 12px;
  margin-bottom: 4px;
  font-size: 12px;
  color: #666;
}

.product-details .warehouse::before {
  content: '仓库: ';
}

.product-details .reason::before {
  content: '原因: ';
}

.product-summary {
  font-size: 14px;
  color: #666;
}

.skus-list {
  background: #f9f9f9;
  border-radius: 4px;
  padding: 8px;
}

.sku-item {
  display: flex;
  padding: 8px 12px;
  background: white;
  border-radius: 4px;
  margin-bottom: 8px;
}

.sku-item:last-child {
  margin-bottom: 0;
}

.sku-info {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  justify-content: space-between;
}

.sku-specs {
  display: flex;
  gap: 8px;
  min-width: 120px;
}

.sku-specs .color,
.sku-specs .size {
  background: #f0f0f0;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
}

.sku-quantity {
  min-width: 60px;
  font-weight: 500;
}

.sku-price,
.sku-total {
  font-size: 12px;
  color: #666;
  min-width: 80px;
}

.sku-total {
  font-weight: 500;
  color: #333;
}

.sku-actions {
  display: flex;
  gap: 4px;
  align-items: center;
  flex-shrink: 0;
}

.pagination-container {
  margin-top: 24px;
  text-align: center;
}
</style>